import { createAsync, query } from '@solidjs/router';
import { createUniqueId, Index, splitProps } from 'solid-js';
import { cn, tv } from 'tailwind-variants';
import type { ComponentProps, ParentProps } from 'solid-js';
import type { VariantProps } from 'tailwind-variants';

type Props = {
	class?: string;
};

export function Skeleton(props: ParentProps<Props>) {
	return <div class={cn('animate-pulse', props.class)}>{props.children}</div>;
}

Skeleton.Text = function (props: { class?: string } & VariantProps<typeof className>) {
	const uid = createUniqueId();
	const data = createAsync(async () => getWidth(uid), { deferStream: true, initialValue: { [uid]: 75 } });

	return (
		<div
			class={cn(className({ darkness: props.darkness }), 'w-(--width) origin-center -scale-y-85', props.class)}
			style={{ '--width': `${data.latest?.[uid]}%` }}
		>
			&nbsp;
		</div>
	);
};

const getWidth = query((id: string) => ({ [id]: Math.floor(Math.random() * 40 + 60) }), 'skeleton-width');

Skeleton.Image = function (props: { class?: string } & VariantProps<typeof className>) {
	return <div class={cn(className({ darkness: props.darkness }), 'size-full')}>&nbsp;</div>;
};

const className = tv({
	base: 'w-full rounded-lg bg-zinc-300 text-base dark:bg-zinc-700',
	variants: {
		darkness: {
			light: 'bg-zinc-200 dark:bg-zinc-800',
			default: 'bg-zinc-300 dark:bg-zinc-700',
			dark: 'bg-zinc-400 dark:bg-zinc-800',
		},
	},
	defaultVariants: {
		darkness: 'default',
	},
});

export function SkeletonText(props: ComponentProps<typeof Skeleton.Text> & { lines?: number }) {
	const [, spread] = splitProps(props, ['lines']);
	return (
		<Skeleton>
			<Index each={Array.from({ length: props.lines ?? 1 }, (_, i) => i)}>{() => <Skeleton.Text {...spread} />}</Index>
		</Skeleton>
	);
}
